import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { mockCustomers } from "../mock";
import { Customer } from "../types";
import { Search } from "lucide-react";

const CustomerLookup: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null
  );
  const [searchResults, setSearchResults] = useState<Customer[]>([]);

  const handleSearch = (): void => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    const results = mockCustomers.filter(
      (customer) =>
        customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.accountNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.meterId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.phone.includes(searchTerm)
    );

    setSearchResults(results as Customer[]);
    if (results.length === 1) {
      setSelectedCustomer(results[0] as Customer);
    }
  };

  const selectCustomer = (customer: Customer): void => {
    setSelectedCustomer(customer);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Customer Lookup</CardTitle>
          <CardDescription>
            Search customers by Meter ID, Account Number, Name, or Phone
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex space-x-2">
            <Input
              placeholder="Enter Meter ID, Account No., Name, or Phone..."
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearchTerm(e.target.value)
              }
              onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) =>
                e.key === "Enter" && handleSearch()
              }
              className="flex-1"
            />
            <Button onClick={handleSearch}>
              <Search className="h-4 w-4 mr-2" />
              Search
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Search Results */}
      {searchResults.length > 0 && !selectedCustomer && (
        <Card>
          <CardHeader>
            <CardTitle>Search Results ({searchResults.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {searchResults.map((customer: Customer) => (
                <div
                  key={customer.id}
                  className="p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => selectCustomer(customer)}>
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-medium">{customer.name}</h4>
                      <p className="text-sm text-gray-600">
                        {customer.address}
                      </p>
                      <div className="flex space-x-4 text-xs text-gray-500 mt-1">
                        <span>Account: {customer.accountNo}</span>
                        <span>Meter: {customer.meterId}</span>
                        <span>Phone: {customer.phone}</span>
                      </div>
                    </div>
                    <Button variant="outline" size="sm">
                      View Details
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Customer Details */}
      {selectedCustomer && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Customer Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold">
                  {selectedCustomer.name}
                </h3>
                <p className="text-gray-600">{selectedCustomer.address}</p>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium">Account Number:</span>
                  <p className="text-gray-600">{selectedCustomer.accountNo}</p>
                </div>
                <div>
                  <span className="font-medium">Meter ID:</span>
                  <p className="text-gray-600">{selectedCustomer.meterId}</p>
                </div>
                <div>
                  <span className="font-medium">Phone:</span>
                  <p className="text-gray-600">{selectedCustomer.phone}</p>
                </div>
                <div>
                  <span className="font-medium">Device Type:</span>
                  <p className="text-gray-600">{selectedCustomer.deviceType}</p>
                </div>
              </div>

              <div>
                <span className="font-medium">Location:</span>
                <p className="text-gray-600">
                  Lat: {selectedCustomer.location.lat}, Lng:{" "}
                  {selectedCustomer.location.lng}
                </p>
              </div>

              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setSelectedCustomer(null)}>
                  Back to Search
                </Button>
                <Button>Create Service Order</Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Complaint History</CardTitle>
              <CardDescription>
                {selectedCustomer.complaintHistory.length > 0
                  ? `${selectedCustomer.complaintHistory.length} previous complaint(s)`
                  : "No previous complaints"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {selectedCustomer.complaintHistory.length > 0 ? (
                <div className="space-y-3">
                  {selectedCustomer.complaintHistory.map((complaint) => (
                    <div key={complaint.id} className="p-3 border rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-sm font-medium">
                          {new Date(complaint.date).toLocaleDateString()}
                        </span>
                        <Badge
                          className={
                            complaint.status === "resolved"
                              ? "bg-green-100 text-green-800"
                              : complaint.status === "open"
                              ? "bg-red-100 text-red-800"
                              : "bg-yellow-100 text-yellow-800"
                          }>
                          {complaint.status.toUpperCase()}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600">
                        {complaint.description}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500">
                  <p>No complaint history available</p>
                  <p className="text-sm">
                    This customer has no previous issues recorded
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default CustomerLookup;
